import React, { createContext, useCallback, useContext, useState } from 'react'
import { useSnackbar } from 'notistack'
import { useAuth } from 'lib/auth'
import { flags, useFeatureFlags } from 'lib/feature-flags'
import { firstName, promotionCoach } from 'lib/stream-messaging/promotions'
import { usePromotionPreference, useSetPromotionPreference } from './api-hooks'
import PromotionsSheet from './promotions-sheet'

const PromotionsContext = createContext(null)

export const usePromotions = () => useContext(PromotionsContext)

export const PromotionsController = ({ coach, children }) => {
  const { enabled } = usePromotionPreference(coach.externalId)
  const { setEnabled, saving } = useSetPromotionPreference(coach.externalId)
  const { enqueueSnackbar } = useSnackbar()
  const [sheetOpen, setSheetOpen] = useState(false)
  // Session-only: set when the golfer turns promotions off, cleared when they turn
  // them back on. The controller is keyed by coach, so switching to another
  // channel starts fresh.
  const [turnedOffNotice, setTurnedOffNotice] = useState(false)

  const name = firstName(coach.name)

  const update = useCallback(
    async next => {
      try {
        await setEnabled(next)
      } catch (e) {
        enqueueSnackbar("Couldn't update promotions. Please try again.", {
          variant: 'error',
        })
        return false
      }
      setTurnedOffNotice(!next)
      enqueueSnackbar(`Promotions from ${name} are ${next ? 'on' : 'off'}`, {
        variant: 'success',
      })
      return true
    },
    [setEnabled, enqueueSnackbar, name],
  )

  // Keep the same element tree whether or not the control applies so the
  // channel below is never remounted when the preference finishes loading.
  const value =
    enabled !== null
      ? {
        coachName: name,
        enabled,
        saving,
        showTurnedOffNotice: turnedOffNotice && !enabled,
        openSheet: () => setSheetOpen(true),
        turnOn: () => update(true),
      }
      : null

  return (
    <PromotionsContext.Provider value={value}>
      {children}
      {value && (
        <PromotionsSheet
          open={sheetOpen}
          onClose={() => setSheetOpen(false)}
          coachName={value.coachName}
          enabled={enabled}
          saving={saving}
          onChange={update}
        />
      )}
    </PromotionsContext.Provider>
  )
}

/**
 * Lets a golfer turn off promotions from the coach in a 1:1 conversation.
 * Renders nothing extra (and provides no context) when the flag is off, the
 * viewer is a coach, the chat is not with a single coach, or the preference
 * cannot be loaded.
 */
const PromotionsProvider = ({ channel, children }) => {
  const { user, isCoach } = useAuth()
  const [isEnabledFlag] = useFeatureFlags([
    flags.FLAG_FEAT_MESSAGE_PROMOTION_CONTROLS,
  ])

  const coach = isEnabledFlag ? promotionCoach(channel, user, isCoach) : null

  if (!coach) return children
  return (
    <PromotionsController key={coach.externalId} coach={coach}>
      {children}
    </PromotionsController>
  )
}

export default PromotionsProvider
